import { useState, useEffect, useRef } from "react";
import { z } from "zod";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

// Remplace par le lien iframe de paiement (Stripe / GHL)
export const LIEN_PAIEMENT_IFRAME =
  "https://link.fastpaydirect.com/payment-link/6a7622f6b4331c330fc606fc";

// Préremplit le lien de paiement pour que GHL rattache la transaction au bon contact
const lienPaiementPrerempli = (v: {
  name?: string;
  email?: string;
  phone?: string;
}) => {
  const [firstName, ...rest] = (v.name ?? "").trim().split(/\s+/);
  const url = new URL(LIEN_PAIEMENT_IFRAME);
  url.searchParams.set("email", (v.email ?? "").trim());
  url.searchParams.set("first_name", firstName ?? "");
  url.searchParams.set("last_name", rest.join(" "));
  url.searchParams.set("name", (v.name ?? "").trim());
  url.searchParams.set("phone", (v.phone ?? "").trim());
  return url.toString();
};

const schema = z.object({
  name: z.string().trim().min(2, "Nom requis").max(100),
  email: z.string().trim().email("Courriel invalide").max(255),
  phone: z.string().trim().min(10, "Téléphone invalide").max(30),
  company: z.string().trim().min(2, "Nom de l'entreprise requis").max(120),
  website: z.string().trim().max(200).optional().or(z.literal("")),
  revenue: z.string().trim().max(80).optional().or(z.literal("")),
});

type FormValues = z.infer<typeof schema>;

const emptyForm: FormValues = {
  name: "",
  email: "",
  phone: "",
  company: "",
  website: "",
  revenue: "",
};

type Step = "form" | "paiement" | "merci";

const PlanDeMatchModal = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [step, setStep] = useState<Step>("form");
  const [values, setValues] = useState<FormValues>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>({});
  const [loading, setLoading] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const notified = useRef(false);

  const set = (key: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));

  const handleClose = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      setTimeout(() => {
        setStep("form");
        setValues(emptyForm);
        setErrors({});
        setRequestId(null);
        notified.current = false;
      }, 250);
    }
  };

  // Vérification réelle du paiement via le webhook FastPayDirect
  useEffect(() => {
    if (step !== "paiement" || !requestId) return;

    let cancelled = false;
    const check = async () => {
      const { data, error } = await supabase.functions.invoke(
        `plan-de-match-request?id=${requestId}`,
        { method: "GET" },
      );
      if (cancelled || error) return;
      if ((data as { payment_status?: string } | null)?.payment_status === "paid") {
        setStep("merci");
        if (!notified.current) {
          notified.current = true;
          toast({ title: "Paiement confirmé !", description: "On vous contacte rapidement." });
        }
      }
    };

    const interval = setInterval(check, 5000);
    check();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [step, requestId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<keyof FormValues, string>> = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[issue.path[0] as keyof FormValues] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("plan-de-match-request", {
        body: parsed.data,
      });
      if (error) throw error;
      setRequestId((data as { id: string }).id);

      try {
        await supabase.functions.invoke("notify-new-lead", {
          body: {
            name: parsed.data.name,
            email: parsed.data.email,
            phone: parsed.data.phone,
            company: parsed.data.company,
            goal: `Plan de match 500$ — Site/IG: ${parsed.data.website || "non fourni"} — Revenus annuels: ${parsed.data.revenue || "non fourni"}`,
          },
        });
      } catch {
        // La notification n'est pas bloquante
      }

      setStep("paiement");
    } catch {
      toast({
        title: "Oups",
        description: "Impossible d'enregistrer votre demande. Réessayez dans un instant.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        {step === "form" && (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl">Réserver mon Plan de match</DialogTitle>
              <DialogDescription>
                Quelques informations pour préparer votre analyse, ensuite le paiement de 500 $.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="mt-2 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="pdm-name">Prénom et nom *</Label>
                  <Input id="pdm-name" value={values.name} onChange={set("name")} maxLength={100} />
                  {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pdm-company">Entreprise *</Label>
                  <Input id="pdm-company" value={values.company} onChange={set("company")} maxLength={120} />
                  {errors.company && <p className="text-xs text-destructive">{errors.company}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pdm-email">Courriel *</Label>
                  <Input id="pdm-email" type="email" value={values.email} onChange={set("email")} maxLength={255} />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pdm-phone">Téléphone *</Label>
                  <Input id="pdm-phone" type="tel" value={values.phone} onChange={set("phone")} maxLength={30} />
                  {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pdm-website">Site web ou Instagram</Label>
                  <Input id="pdm-website" value={values.website} onChange={set("website")} maxLength={200} />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="pdm-revenue">Chiffre d'affaires annuel approximatif</Label>
                  <Input id="pdm-revenue" value={values.revenue} onChange={set("revenue")} maxLength={80} />
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="group w-full rounded-full bg-primary py-6 text-base font-semibold text-primary-foreground hover:bg-primary/90"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Continuer vers le paiement
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </Button>
            </form>
          </>
        )}

        {step === "paiement" && (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl">Paiement du Plan de match</DialogTitle>
              <DialogDescription>Montant : 500 $. Paiement sécurisé.</DialogDescription>
            </DialogHeader>

            <div className="mt-2 overflow-hidden rounded-xl border border-border bg-card">
              <iframe
                src={lienPaiementPrerempli(values)}
                title="Paiement Plan de match"
                className="h-[560px] w-full"
                allow="payment"
              />
            </div>

            <div className="mt-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              En attente de la confirmation du paiement...
            </div>
          </>
        )}

        {step === "merci" && (
          <div className="py-6 text-center">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-primary/12 border border-primary/30">
              <Check className="h-6 w-6 text-primary" />
            </div>
            <DialogHeader className="mt-5">
              <DialogTitle className="text-center text-2xl">Merci !</DialogTitle>
              <DialogDescription className="text-center">
                On communique avec vous très bientôt afin de bâtir votre plan de match.
              </DialogDescription>
            </DialogHeader>
            <Button className="mt-6 rounded-full px-8" onClick={() => handleClose(false)}>
              Fermer
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlanDeMatchModal;
